import React, { useMemo } from "react";
import { amphibiousGetIn, amphibiousToJS } from "helpers/objUtils";
import { IDoor } from "helpers/door-collection";
import { ProductCartTile } from "./product-cart-tile";
import { IAddItem } from "actions/collections";
import {
    getCollectionSwatch,
    getViewSettingFields,
    getViewSettingValues,
    IDisplayFields,
} from "helpers/product-collection";
import { AllocationBag, IAllocationDimension } from "helpers/allocation-dimension";
import { IGridParamsQueryParam } from "../product-collection-grid-view/tool-panels";
import { ICurrency } from "../product-collection-grid-view/grid-renderers/pinned-row-renderers";
import { ITileSettings } from "components/product-collection/manage-tile-view/types";
import { ICollectionSwatch, SwatchType } from "constants/color-swatches";
import { Item } from "constants/product-collection";

import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import { IGroupKey } from "components/infinite";
import { ColDef } from "ag-grid-community";
import { List } from "immutable";
import { getTotalsFromEntries } from "containers/legacy-cart/helpers";

const DEFAULT_TILE_IMAGE = "//cdn1.nuorder.com/brands/1womenswholesale_missing.jpg";

const getSelectedSwatch = (product): ICollectionSwatch => {
    const swatch = getCollectionSwatch(product);

    if (!swatch) {
        // no matching swatch, just use high level color instead
        return {
            swatch_type: SwatchType.HEX,
            color: amphibiousGetIn(product, Item.FIELDS.COLOR),
            value: null, // to use color as a value (legacy support)
        };
    }

    return swatch;
};

interface IOwnProps {
    allocationBagRef?: {
        allocationBag: AllocationBag;
    };
    onAddItem?: (params: IAddItem) => void;
    itemsId?: string;
    onLinkProductToItem?: (id: string) => void;
    collectionId: string;
    onOpenDeleteConfirmation?: (itemsid, itemIndex) => void;
    gridApi: any;
    editable: boolean;
    brandEntities: any;
    cardViewFields: IDisplayFields;
    shopViewFields?: IDisplayFields;
    doors: IDoor[];
    formulaFields: IDisplayFields;
    allocationDimensions: IAllocationDimension[];
    gridParams: IGridParamsQueryParam;
    dimensionFilters?: string[][];
    dimensionsPanelApplied: DimensionsPanelReduxState;
    allowDuplicateProduct: boolean;
    merchantCurrency?: ICurrency;
    tileSettings?: ITileSettings;
    rawItemDisplayProperty?: ColDef[];
    data: any;
    isSortable: boolean;
    dataIndex: number;
    tileViewSize: string;
    isServerSide?: boolean;
    isAverageUnitsMode?: boolean;
    groupKeyValues?: List<string>;
    groupedKeys?: IGroupKey[];
    openSizingGrid?: (lineItemId: string, refresh?: boolean) => void;
    onClickProductName?: (productId: string) => void;
    getProductNameLink?: (productId: string) => string;
}

export const ProductCollectionCard: React.FC<IOwnProps> = ({
    onAddItem,
    itemsId,
    onLinkProductToItem,
    collectionId,
    onOpenDeleteConfirmation,
    gridApi,
    editable,
    brandEntities,
    cardViewFields,
    shopViewFields,
    allowDuplicateProduct,
    merchantCurrency,
    tileSettings,
    rawItemDisplayProperty,
    data,
    isSortable,
    dataIndex,
    tileViewSize,
    isServerSide,
    dimensionFilters,
    openSizingGrid,
    onClickProductName,
    getProductNameLink,
}) => {
    const productId = amphibiousGetIn(data, Item.FIELDS.PRODUCT_ID) || null;
    const tertiaryAttributes = [amphibiousGetIn(data, ["fields", "vpn", "string_value"])];
    const primaryImageUrl = amphibiousGetIn(data, ["fields", "image_url", "string_value"]) || DEFAULT_TILE_IMAGE;
    const images = amphibiousGetIn(data, ["fields", "image_urls", "string_value"])
        ? amphibiousGetIn(data, ["fields", "image_urls", "string_value"]).split(",")
        : [];
    const secondaryImageUrl = images.length > 1 ? images[1] : undefined;
    const colorSwatch = getSelectedSwatch(data);
    const tileFieldSchemaHash = getViewSettingFields(tileSettings, rawItemDisplayProperty);
    const tileSettingsValue = getViewSettingValues(tileFieldSchemaHash, data, isServerSide, dimensionFilters);

    const { totalDoors, totalUnits, totalRetail, totalCost } = useMemo(() => {
        const totals = getTotalsFromEntries(amphibiousToJS(data)?.entries || []);
        return {
            totalDoors: 0,
            totalUnits: totals.units,
            totalRetail: totals.retail,
            totalCost: totals.wholesale,
        };
    }, [data]);

    return (
        <ProductCartTile
            brandEntities={brandEntities}
            cardViewFields={cardViewFields}
            shopViewFields={shopViewFields}
            colorSwatch={colorSwatch}
            data={data}
            editable={editable}
            imageUrl={primaryImageUrl}
            gridApi={gridApi}
            isSortable={isSortable}
            productId={productId}
            secondaryImageUrl={secondaryImageUrl}
            showLinking={true}
            tertiaryAttributes={tertiaryAttributes}
            totalUnits={totalUnits}
            totalDoors={totalDoors}
            dimensionFilters={dimensionFilters}
            totalRetail={totalRetail}
            totalCost={totalCost}
            onAddItem={onAddItem}
            itemsId={itemsId}
            onLinkProductToItem={onLinkProductToItem}
            collectionId={collectionId}
            dataIndex={dataIndex}
            onOpenDeleteConfirmation={onOpenDeleteConfirmation}
            allowDuplicateProduct={allowDuplicateProduct}
            merchantCurrency={merchantCurrency}
            tileSettings={tileSettings}
            tileSettingsValue={tileSettingsValue}
            tileViewSize={tileViewSize}
            isServerSide={isServerSide}
            openSizingGrid={openSizingGrid}
            onClickProductName={onClickProductName}
            getProductNameLink={getProductNameLink}
        />
    );
};
